import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Avatar,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Send as SendIcon,
  Inbox as InboxIcon,
  Visibility as ViewIcon,
  AccessTime as TimeIcon,
  AttachFile as AttachmentIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import api from '../../utils/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ p: 3 }}>
    {value === index && children}
  </Box>
);

interface StatCardProps {
  title: string;
  value: string | number;
  change: number;
  icon: React.ReactNode;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, change, icon, color }) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4" fontWeight={600}>
            {value}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            {change >= 0 ? (
              <TrendingUpIcon sx={{ fontSize: 16, color: 'success.main' }} />
            ) : (
              <TrendingDownIcon sx={{ fontSize: 16, color: 'error.main' }} />
            )}
            <Typography
              variant="caption"
              sx={{ color: change >= 0 ? 'success.main' : 'error.main' }}
            >
              {Math.abs(change)}% vs last period
            </Typography>
          </Box>
        </Box>
        <Avatar sx={{ bgcolor: color, width: 48, height: 48 }}>
          {icon}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const AnalyticsPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [period, setPeriod] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [emailStats, setEmailStats] = useState({
    sent: 0,
    received: 0,
    openRate: 0,
    avgResponseTime: 'N/A',
    sentChange: 0,
    receivedChange: 0,
    readChange: 0,
    responseChange: 0,
  });

  const [topCorrespondents, setTopCorrespondents] = useState<
    { email: string; name: string; sent: number; received: number }[]
  >([]);

  const [weeklyVolume, setWeeklyVolume] = useState<
    { day: string; sent: number; received: number }[]
  >([]);

  const [productivityData, setProductivityData] = useState({
    peakHours: ['N/A'],
    avgEmailsPerDay: 0,
    longestThread: 0,
    attachmentsSent: 0,
    attachmentSize: '0 MB',
  });

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [overviewRes, correspondentsRes, volumeRes, productivityRes] = await Promise.all([
        api.get('/emails/analytics/overview', { params: { timeRange: period } }),
        api.get('/emails/analytics/correspondents', { params: { timeRange: period } }),
        api.get('/emails/analytics/volume', { params: { timeRange: period } }),
        api.get('/emails/analytics/productivity', { params: { timeRange: period } }),
      ]);

      setEmailStats(overviewRes.data);
      setTopCorrespondents(correspondentsRes.data);
      setWeeklyVolume(volumeRes.data);
      setProductivityData(productivityRes.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return (
    <Box sx={{ p: 3, height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>
            Email Analytics
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Track email performance and productivity insights
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Time Period</InputLabel>
            <Select
              value={period}
              label="Time Period"
              onChange={(e) => setPeriod(e.target.value)}
            >
              <MenuItem value="7d">Last 7 days</MenuItem>
              <MenuItem value="30d">Last 30 days</MenuItem>
              <MenuItem value="90d">Last 90 days</MenuItem>
              <MenuItem value="1y">Last year</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title="Export analytics">
            <IconButton>
              <DownloadIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
      <>
      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Emails Sent"
            value={emailStats.sent}
            change={emailStats.sentChange}
            icon={<SendIcon />}
            color="#1976d2"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Emails Received"
            value={emailStats.received}
            change={emailStats.receivedChange}
            icon={<InboxIcon />}
            color="#2e7d32"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Open Rate"
            value={`${emailStats.openRate}%`}
            change={emailStats.readChange}
            icon={<ViewIcon />}
            color="#ed6c02"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Avg Response Time"
            value={emailStats.avgResponseTime}
            change={emailStats.responseChange}
            icon={<TimeIcon />}
            color="#9c27b0"
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ borderRadius: 2 }}>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Top Correspondents" />
          <Tab label="Productivity" />
          <Tab label="Weekly Volume" />
        </Tabs>

        {/* Top Correspondents Tab */}
        <TabPanel value={tabValue} index={0}>
          <Typography variant="subtitle2" gutterBottom>
            Your Top Email Contacts
          </Typography>
          <List>
            {topCorrespondents.map((contact, index) => (
              <ListItem key={contact.email} divider={index < topCorrespondents.length - 1}>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: `hsl(${index * 50}, 60%, 50%)` }}>
                    {contact.name.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={contact.name}
                  secondary={contact.email}
                />
                <Box sx={{ display: 'flex', gap: 3, mr: 2 }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight={600}>{contact.sent}</Typography>
                    <Typography variant="caption" color="text.secondary">Sent</Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="body2" fontWeight={600}>{contact.received}</Typography>
                    <Typography variant="caption" color="text.secondary">Received</Typography>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </TabPanel>

        {/* Productivity Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Email Productivity Insights
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemText
                        primary="Peak Email Hours"
                        secondary={productivityData.peakHours.join(', ')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Average Emails Per Day"
                        secondary={productivityData.avgEmailsPerDay}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Longest Email Thread"
                        secondary={`${productivityData.longestThread} messages`}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    Attachment Statistics
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#e3f2fd' }}>
                          <AttachmentIcon color="primary" />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary="Attachments Sent"
                        secondary={productivityData.attachmentsSent}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Total Size"
                        secondary={productivityData.attachmentSize}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Weekly Volume Tab */}
        <TabPanel value={tabValue} index={2}>
          <Typography variant="subtitle2" gutterBottom>
            Weekly Email Volume
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Sent vs Received emails by day
          </Typography>
          <Box sx={{ height: 300 }}>
            {(() => {
              const maxVal = Math.max(...weeklyVolume.map(d => Math.max(d.sent, d.received)), 1);
              return weeklyVolume.map((day) => (
              <Box key={day.day} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ width: 40, fontWeight: 500 }}>
                  {day.day}
                </Typography>
                <Box sx={{ flex: 1, display: 'flex', gap: 1 }}>
                  <Tooltip title={`Sent: ${day.sent}`}>
                    <Box
                      sx={{
                        height: 24,
                        width: `${(day.sent / maxVal) * 100}%`,
                        minWidth: day.sent > 0 ? 24 : 0,
                        bgcolor: '#1976d2',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        pr: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#fff' }}>
                        {day.sent}
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title={`Received: ${day.received}`}>
                    <Box
                      sx={{
                        height: 24,
                        width: `${(day.received / maxVal) * 100}%`,
                        minWidth: day.received > 0 ? 24 : 0,
                        bgcolor: '#2e7d32',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        pr: 1,
                      }}
                    >
                      <Typography variant="caption" sx={{ color: '#fff' }}>
                        {day.received}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
            ));
            })()}
          </Box>
          <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#1976d2', borderRadius: 1 }} />
              <Typography variant="caption">Sent</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 12, height: 12, bgcolor: '#2e7d32', borderRadius: 1 }} />
              <Typography variant="caption">Received</Typography>
            </Box>
          </Box>
        </TabPanel>
      </Paper>
      </>
      )}
    </Box>
  );
};

export default AnalyticsPage;
